import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { ArrowRight, Zap, Workflow, Timer, Target } from 'lucide-react';
import { ImageWithFallback } from '../figma/ImageWithFallback';

export function ProcessAutomation() {
  const automationTypes = [
    {
      icon: <Workflow className="h-8 w-8 text-orange-600" />,
      title: "Workflow Automation",
      description: "Automate repetitive tasks and approvals",
      examples: ["Lead assignment", "Invoice approvals", "Task routing"]
    },
    {
      icon: <Timer className="h-8 w-8 text-orange-600" />,
      title: "Scheduled Actions",
      description: "Time-based automated processes",
      examples: ["Report generation", "Follow-up emails", "Data backups"]
    },
    {
      icon: <Target className="h-8 w-8 text-orange-600" />,
      title: "Smart Triggers",
      description: "Condition-based automation rules",
      examples: ["Status updates", "Notifications", "Record updates"]
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Content */}
          <div className="space-y-8">
            <div>
              <div className="inline-flex items-center px-4 py-2 bg-orange-100 text-orange-800 rounded-full mb-4">
                <Zap className="h-4 w-4 mr-2" />
                Zoho Flow & Automation
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Automate Your Way to Efficiency
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                Eliminate manual tasks, reduce errors, and boost productivity with intelligent 
                process automation using Zoho Flow and other automation tools tailored to your business workflows.
              </p>
            </div>

            <div className="space-y-6">
              {automationTypes.map((type, index) => (
                <Card key={index} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0 p-3 bg-orange-50 rounded-lg">
                        {type.icon}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 mb-2">{type.title}</h3>
                        <p className="text-gray-600 mb-3">{type.description}</p>
                        <div className="flex flex-wrap gap-2">
                          {type.examples.map((example, idx) => (
                            <span key={idx} className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm">
                              {example}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="bg-gradient-to-r from-orange-600 to-red-600 rounded-xl p-6 text-white">
              <h3 className="font-bold text-xl mb-2">Save 40+ Hours Per Week</h3>
              <p className="text-orange-100 mb-4">
                Our clients typically save 40+ hours per week through intelligent automation with Zoho Flow
              </p>
              <a href="https://consult.optiwizer.com/#/book_consultation" target="_blank" rel="noopener noreferrer">
                <Button variant="secondary" className="bg-black text-white border-2 border-black hover:bg-yellow-400 hover:text-black text-base font-bold py-3 px-8 shadow-lg">
                  Get Your Automation Quote
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </a>
            </div>
          </div>

          {/* Image */}
          <div className="relative">
            <ImageWithFallback 
              src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=600&h=500&fit=crop"
              alt="Process automation dashboard"
              className="rounded-2xl shadow-xl w-full h-[500px] object-cover"
            />
            
            {/* Floating Automation Stats */}
            <div className="absolute top-6 left-6 bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-lg">
              <div className="flex items-center mb-2">
                <Zap className="h-6 w-6 mr-2 text-orange-600" />
                <span className="text-sm font-medium text-gray-700">Automation</span>
              </div>
              <div className="text-2xl font-bold text-orange-600">75%</div>
              <div className="text-sm text-gray-600">Time Saved</div>
            </div>
            
            <div className="absolute bottom-6 right-6 bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-lg">
              <div className="text-2xl font-bold text-green-600">99.9%</div>
              <div className="text-sm text-gray-600">Accuracy</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}